/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from 'react'
import { Button, Modal, Select, Input } from 'antd'
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import {
  currentTaskUserState,
  currentTaskUserMobileState,
  showTaskUserDialogState,
  taskState,
  currentAccountState,
} from '../recoil/atoms'
import { useTranslation } from 'react-i18next'
import { CreateUserUseCase, IUserInfo, QueryUsersUseCase } from '@axelity/actasign-sic'
import * as yup from 'yup'
import SectionTitle from '../elements/SectionTitle'
import FieldTitle from '../elements/FieldTitle'

const { Option } = Select

const TaskUser = () => {
  const { t } = useTranslation(['common', 'taskUser'])

  const setCurrentTaskUser = useSetRecoilState(currentTaskUserState)
  const resetCurrentTaskUser = useResetRecoilState(currentTaskUserState)

  const setCurrentTaskUserMobile = useSetRecoilState(currentTaskUserMobileState)
  const resetCurrentTaskUserMobile = useResetRecoilState(currentTaskUserMobileState)

  const task = useRecoilValue(taskState)
  const currentAccount = useRecoilValue(currentAccountState)

  const emailRef = useRef(null)
  const firstNameRef = useRef(null)

  const [firstName, setFirstName] = useState<string>('')
  const [familyName, setFamilyName] = useState<string>('')
  const [emailAddress, setEmailAddress] = useState<string>('')
  const [language, setLanguage] = useState<string>('')
  const [mobileNumber, setMobileNumber] = useState<string>('')

  const [isUpdating, setUpdating] = useState<boolean>(false)

  const [showDialog, setShowDialog] = useRecoilState(showTaskUserDialogState)

  const [canSearch, setCanSearch] = useState<boolean>(false)
  const [searchExecuted, setSearchExecuted] = useState<boolean>(false)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [userExists, setUserExists] = useState<boolean>(false)

  const [mobileError, setMobileError] = useState<string>('')

  const [taskUser, setTaskUser] = useState<IUserInfo>(undefined)
  const [accountMembers, setAccountMembers] = useState<IUserInfo[]>([])
  const [isCheckingMembership, setIsCheckingMembership] = useState<boolean>(false)

  // Check if mobile number is required for delegation
  const isMobileRequired = (): boolean => {
    if (!task || !currentAccount || !taskUser) return false

    // Mobile required only for AES/QES signatures
    const isAESorQES = task.trustLevel === 'AES' || task.trustLevel === 'QES'
    if (!isAESorQES) return false

    // Check if user is member of current account by checking accountMembers list
    const isMember = accountMembers.some(member => member._id === taskUser._id)

    if (isMember) return false

    // Check if user already has mobile number
    const hasMobile = !!taskUser?.mobile?.phoneNumber
    if (hasMobile) return false

    return true
  }

  // Validate E.164 mobile number format with length constraints
  const validateMobileNumber = (value: string): boolean => {
    // Pattern: + followed by country code and number
    // Total length (including +): min 10, max 14
    const e164Pattern = /^\+[1-9]\d{8,13}$/
    return e164Pattern.test(value)
  }

  const handleChangeMobileNumber = (value: string) => {
    setMobileNumber(value)
    setMobileError('')

    if (value && !validateMobileNumber(value)) {
      setMobileError(t('taskUser:mobileNumberInvalid'))
    }
  }

  const lookup = () => {
    if (!canSearch) return

    setLoading(true)

    new QueryUsersUseCase()
      .query({
        emailAddress,
      })
      .then((users) => {
        if (users.length > 0) {
          const _userInfo = users[0]
          setTaskUser(_userInfo)

          setFirstName(_userInfo.firstName)
          setFamilyName(_userInfo.familyName)
          setEmailAddress(_userInfo.email?.emailAddress || '')
          setLanguage(_userInfo.language)
          setUserExists(true)
        } else {
          setFirstName('')
          setFamilyName('')
          setLanguage('')
        }
        setSearchExecuted(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }
  const handleChangeEmailAddress = (value: string) => {
    setEmailAddress(value)
    setSearchExecuted(false)
    if (
      yup
        .string()
        .required()
        .matches(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        )
        .isValidSync(value)
    ) {
      setCanSearch(true)
    }
  }

  const handleOk = async () => {
    // Validate mobile if required
    if (isMobileRequired()) {
      if (!mobileNumber) {
        setMobileError(t('taskUser:mobileNumberRequired'))
        return
      }
      if (!validateMobileNumber(mobileNumber)) {
        setMobileError(t('taskUser:mobileNumberInvalid'))
        return
      }
    }

    setUpdating(true)

    try {
      let _userInfo: IUserInfo

      if (!taskUser) {
        // Create new user
        _userInfo = await new CreateUserUseCase().create({
          emailAddress,
          familyName,
          firstName,
          language,
        })
      } else {
        _userInfo = taskUser
      }

      // Store mobile number for delegation (will be used in Task.tsx)
      if (isMobileRequired() && mobileNumber) {
        setCurrentTaskUserMobile(mobileNumber)
      } else {
        setCurrentTaskUserMobile('')
      }

      setCurrentTaskUser(_userInfo)
      setShowDialog(false)
    } catch (error) {
      console.error(error)
    } finally {
      setUpdating(false)
    }
  }

  const handleCancel = () => {
    resetCurrentTaskUser()
    resetCurrentTaskUserMobile()
    setAccountMembers([]) // Clear account members cache
    setShowDialog(false)
  }

  const handleReset = () => {
    setFirstName('')
    setFamilyName('')
    setEmailAddress('')
    setLanguage('')
    setMobileNumber('')
    setMobileError('')
    setCanSearch(false)
    setLoading(false)
    setUserExists(false)
    setSearchExecuted(false)
    resetCurrentTaskUser()
    resetCurrentTaskUserMobile()
    setTaskUser(undefined)
  }

  // Fetch account members when dialog opens
  useEffect(() => {
    if (showDialog && currentAccount && !isCheckingMembership && accountMembers.length === 0) {
      setIsCheckingMembership(true)
      new QueryUsersUseCase()
        .query({
          account: currentAccount._id,
        })
        .then((members: IUserInfo[]) => {
          setAccountMembers(members)
          console.log('Fetched account members:', members.length)
        })
        .catch((error) => {
          console.error('Failed to fetch account members:', error)
        })
        .finally(() => {
          setIsCheckingMembership(false)
        })
    }
  }, [showDialog, currentAccount])

  useEffect(() => {
    if (searchExecuted && !userExists) {
      firstNameRef.current.focus()
    }
  }, [searchExecuted, userExists])

  useEffect(() => {
    if (taskUser) {
      setFirstName(taskUser.firstName)
      setFamilyName(taskUser.familyName)
      setEmailAddress(taskUser.email.emailAddress)
      setLanguage(taskUser.language)
      setUserExists(true)
      setSearchExecuted(true)
    } else {
      handleReset()
    }
  }, [taskUser])

  return (
    <Modal
      title={t('taskUser:title')}
      open={showDialog}
      footer={
        !taskUser
          ? [
              <Button className="rounded font-semibold" key="reset" type="default" onClick={handleReset}>
                {t('taskUser:action.reset')}
              </Button>,
              <Button className="rounded font-semibold" key="cancel" onClick={handleCancel}>
                {t('taskUser:action.cancel')}
              </Button>,
              <Button
                className="rounded font-semibold"
                key="apply"
                type="primary"
                onClick={handleOk}
                disabled={!searchExecuted || (isMobileRequired() && (!mobileNumber || !!mobileError))}
                loading={isUpdating}
              >
                {t('taskUser:action.apply')}
              </Button>,
            ]
          : [
              <Button className="rounded font-semibold" key="cancel" onClick={handleCancel}>
                {t('taskUser:action.cancel')}
              </Button>,
              <Button
                className="rounded font-semibold"
                key="apply"
                type="primary"
                onClick={handleOk}
                disabled={!searchExecuted || (isMobileRequired() && (!mobileNumber || !!mobileError))}
                loading={isUpdating}
              >
                {t('taskUser:action.apply')}
              </Button>,
            ]
      }
    >
      <div className="mt-2">
        <FieldTitle title={t('taskUser:emailAddress')} />
        <div className="flex w-full">
          <Input
            className="rounded"
            type="email"
            size="large"
            disabled={userExists}
            value={emailAddress}
            onChange={(e) => handleChangeEmailAddress(e.target.value)}
            onPressEnter={lookup}
            ref={emailRef}
          ></Input>

          <Button
            className="ml-2 rounded font-semibold"
            disabled={!canSearch}
            size="large"
            type="primary"
            onClick={(e) => lookup()}
            loading={isLoading}
          >
            {t('taskUser:action.select')}
          </Button>
        </div>

        {searchExecuted ? (
          <div>
            {!userExists ? <SectionTitle title={t('taskUser:newUser')} /> : null}

            <FieldTitle title={t('taskUser:firstName')} />
            <div className="inline-flex w-full">
              <Input
                ref={firstNameRef}
                className="rounded"
                autoComplete="first-name"
                type="text"
                size="large"
                disabled={userExists}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              ></Input>
            </div>

            <FieldTitle title={t('taskUser:familyName')} />
            <div className="inline-flex w-full">
              <Input
                className="rounded"
                autoComplete="family-name"
                type="text"
                size="large"
                disabled={userExists}
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
              ></Input>
            </div>

            {/**
             * Language
             */}
            <FieldTitle title={t('taskUser:language')} />
            <div className="inline-flex w-full">
              <div className="w-full">
                <Select
                  className="rounded"
                  disabled={userExists}
                  value={language}
                  size="large"
                  style={{ width: '100%' }}
                  onChange={(value) => setLanguage(value)}
                >
                  <Option value="de">{t('common:language.german')}</Option>
                  <Option value="en">{t('common:language.english')}</Option>
                  {/* <Option value="fr">{t('common:language.french')}</Option> */}
                </Select>
              </div>
            </div>

            {/**
             * Mobile Number (conditionally shown for AES/QES non-members without mobile)
             */}
            {isMobileRequired() ? (
              <>
                <FieldTitle title={t('taskUser:mobileNumber')} />
                <div className="inline-flex w-full">
                  <Input
                    className="rounded"
                    type="tel"
                    size="large"
                    value={mobileNumber}
                    onChange={(e) => handleChangeMobileNumber(e.target.value)}
                    placeholder="+41791234567"
                    status={mobileError ? 'error' : ''}
                  ></Input>
                </div>
                {mobileError ? (
                  <div className="text-red-500 text-sm mt-1">{mobileError}</div>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

export default TaskUser
